import { useEffect } from "react";
import { chaves, queryClient } from "./consultas";
import { tokenAtual } from "./api";
import { marcarEscrevendo } from "./escrevendo";

/**
 * Ouve o servidor e atualiza SÓ o que mudou.
 *
 * O servidor avisa "a conversa X mudou" e nada mais. Aqui isso vira: a lista e
 * aquela conversa estão velhas. Se estiverem na tela, são buscadas de novo por
 * trás e trocadas quando chegam — sem esqueleto, sem piscar. Se não estiverem,
 * só ficam marcadas, e são buscadas quando alguém abrir.
 *
 * `fetch` com leitura em fluxo, e não `EventSource`: EventSource não manda
 * cabeçalho, e o token teria de ir na URL — onde fica gravado em log.
 */
export function useTempoReal() {
  useEffect(() => {
    let ativo = true;
    let controle: AbortController | null = null;

    // Um paciente manda três mensagens seguidas e o agente responde: são
    // quatro avisos em dois segundos. Junta os de 150ms numa busca só.
    const pendentes = new Set<string>();
    let agendado: ReturnType<typeof setTimeout> | null = null;
    const invalidar = (telefone: string) => {
      pendentes.add(telefone);
      agendado ??= setTimeout(() => {
        agendado = null;
        void queryClient.invalidateQueries({ queryKey: chaves.conversas });
        for (const t of pendentes) void queryClient.invalidateQueries({ queryKey: chaves.conversa(t) });
        pendentes.clear();
      }, 150);
    };

    async function conectar() {
      let tentativa = 0;
      let jaConectou = false;
      while (ativo) {
        try {
          const token = await tokenAtual();
          controle = new AbortController();
          const r = await fetch("/api/painel/eventos", {
            headers: token ? { authorization: `Bearer ${token}` } : {},
            signal: controle.signal,
          });
          if (!r.ok || !r.body) throw new Error(`eventos: ${r.status}`);

          // Voltou depois de cair: o que mudou nesse meio tempo não foi avisado.
          if (jaConectou) {
            void queryClient.invalidateQueries({ queryKey: chaves.conversas });
            void queryClient.invalidateQueries({ queryKey: ["conversa"] });
          }
          jaConectou = true;
          tentativa = 0;

          const leitor = r.body.pipeThrough(new TextDecoderStream()).getReader();
          let resto = "";
          for (;;) {
            const { value, done } = await leitor.read();
            if (done) break;
            resto += value;
            let fim: number;
            while ((fim = resto.indexOf("\n\n")) >= 0) {
              const bloco = resto.slice(0, fim);
              resto = resto.slice(fim + 2);
              const evento = /^event: (.+)$/m.exec(bloco)?.[1];
              const dados = /^data: (.+)$/m.exec(bloco)?.[1];
              if (evento === "mudou" && dados) {
                const { telefone } = JSON.parse(dados) as { telefone: string };
                invalidar(telefone);
              } else if (evento === "escrevendo" && dados) {
                const { telefone, ativo } = JSON.parse(dados) as { telefone: string; ativo: boolean };
                marcarEscrevendo(telefone, ativo);
              }
            }
          }
        } catch {
          if (!ativo) return;
        }
        // O servidor fecha a cada 10 minutos (renova o token); queda de rede
        // espera um pouco mais a cada vez, até 30s.
        const espera = tentativa === 0 ? 250 : Math.min(30_000, 1000 * 2 ** tentativa);
        tentativa++;
        await new Promise((r) => setTimeout(r, espera));
      }
    }

    void conectar();
    return () => {
      ativo = false;
      controle?.abort();
      if (agendado) clearTimeout(agendado);
    };
  }, []);
}
